import React, { useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import useDashboardData from "../hooks/useDashboardData";
import HomeIntelligenceCard from "../components/dashboard/HomeIntelligenceCard";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import TodaysChange from "../components/dashboard/TodaysChange";
import LivePowerCard from "../components/dashboard/LivePowerCard";
import EnergySection from "../components/dashboard/EnergySection";
import WaterSnapshotCard from "../components/dashboard/WaterSnapshotCard";
import WaterSection from "../components/dashboard/WaterSection";
import WeatherMini from "../components/dashboard/WeatherMini";

/**
 * Dashboard — pure layout. All data orchestration lives in
 * `useDashboardData`; weather sync is a single inline action.
 */
export default function Dashboard() {
  const { currentSite } = useSite();
  const { data, latestEnergy, latestWater, sources, loading, err, reload } =
    useDashboardData(currentSite);
  const [syncing, setSyncing] = useState(false);
  const [syncErr, setSyncErr] = useState(null);

  const syncWeather = async () => {
    if (!currentSite) return;
    setSyncing(true);
    setSyncErr(null);
    try {
      await api.post(`/sites/${currentSite.id}/weather/sync?days=30`);
      await reload();
    } catch (e) {
      setSyncErr(e.userMessage);
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !data) {
    return <div className="text-muted-foreground text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;
  }
  const errorMessage = err || syncErr;
  if (errorMessage) return <div className="text-[#EF4444] text-sm" data-testid="dashboard-error">{errorMessage}</div>;
  if (!data) return null;

  return (
    <div className="space-y-10 pb-12" data-testid="dashboard-page">
      <DashboardHeader site={currentSite} days={data.days} onRefresh={reload} />
      <HomeIntelligenceCard sources={sources} />
      <LivePowerCard siteId={currentSite?.id} />
      <TodaysChange
        latestEnergy={latestEnergy}
        latestWater={latestWater}
        latestWeather={data.latest_weather}
      />
      <EnergySection
        kpis={data.energy_kpis}
        today={data.energy_today}
        confidence={data.confidence}
      />
      <WaterSnapshotCard siteId={currentSite?.id} />
      <WaterSection kpis={data.water_kpis} confidence={data.confidence} site={currentSite} />
      <WeatherMini
        latest={data.latest_weather}
        confidence={data.confidence.weather}
        syncing={syncing}
        onSync={syncWeather}
      />
    </div>
  );
}
